import React, { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { Sidebar } from '@/components/layout/Sidebar';
import { RecentActivity } from '@/components/dashboard/RecentActivity';
import { useResponsive } from '@/hooks/useResponsive';
import { userService, UserStats, TechnicalDepartmentStats } from '@/services/userService';
import { libraryService, Book, LibraryStats } from '@/services/libraryService';
import { vehicleService, VehicleSummary } from '@/services/vehicleService';
import { approvalsService, PendingApprovalsSummary } from '@/services/approvalsService';
import { systemService, SystemMetrics, PerformanceOverview, SecurityStats } from '@/services/systemService';
import { activityService } from '@/services/activityService';
import { 
  Users, 
  TrendingUp, 
  BarChart3,
  Clock,
  BookOpen,
  Car,
  CheckCircle,
  Bell
} from 'lucide-react';

export function Dashboard() {
  const { user } = useAuthStore();
  const { isMobile } = useResponsive();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [technicalDeptStats, setTechnicalDeptStats] = useState<TechnicalDepartmentStats | null>(null);
  const [libraryStats, setLibraryStats] = useState<LibraryStats | null>(null);
  const [vehicleStats, setVehicleStats] = useState<VehicleSummary | null>(null);
  const [approvalsStats, setApprovalsStats] = useState<PendingApprovalsSummary | null>(null);
  const [systemMetrics, setSystemMetrics] = useState<SystemMetrics | null>(null);
  const [performanceData, setPerformanceData] = useState<PerformanceOverview | null>(null);
  const [securityStats, setSecurityStats] = useState<SecurityStats | null>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const toggleSidebar = () => {
    // Only toggle on mobile, desktop stays fixed
    if (isMobile) {
      setMobileMenuOpen(!mobileMenuOpen);
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const openMobileMenu = () => {
    setMobileMenuOpen(true);
  };

  // Fetch dashboard data - non-blocking initial render
  useEffect(() => {
    // Set loading to false immediately to render UI
    setLoading(false);
    
    // Fetch data in background without blocking render
    const fetchDashboardData = async () => {
      try {
        const isTechnicalHOD = user?.roles?.some(role => role.name === 'Technical HOD');
        
        // Use Promise.allSettled to prevent one slow API from blocking others
        const results = await Promise.allSettled([
          userService.getUserStats(),
          libraryService.getLibraryStats(),
          vehicleService.getVehicleSummary(),
          approvalsService.getPendingApprovalsSummary(),
          systemService.getSystemMetrics(),
          systemService.getPerformanceOverview(),
          systemService.getSecurityStats(),
          activityService.getDashboardNotifications(0, 50)
        ]);
        
        // Process results
        if (results[0].status === 'fulfilled') setUserStats(results[0].value);
        if (results[1].status === 'fulfilled') setLibraryStats(results[1].value);
        if (results[2].status === 'fulfilled') setVehicleStats(results[2].value);
        if (results[3].status === 'fulfilled') setApprovalsStats(results[3].value);
        if (results[4].status === 'fulfilled') setSystemMetrics(results[4].value);
        if (results[5].status === 'fulfilled') setPerformanceData(results[5].value);
        if (results[6].status === 'fulfilled') setSecurityStats(results[6].value);
        if (results[7].status === 'fulfilled') setNotifications(results[7].value);
        
        // Fetch technical department stats separately if user is Technical HOD
        if (isTechnicalHOD) {
          try {
            const technicalDeptData = await userService.getTechnicalDepartmentCount();
            setTechnicalDeptStats(technicalDeptData);
          } catch (error) {
            // Silently fail - non-critical data
          }
        }
      } catch (error) {
        // Silently handle errors - don't block UI
        console.error('Failed to fetch dashboard data:', error);
      }
    };

    fetchDashboardData();
  }, [user]);

  // Desktop: always show sidebar, Mobile: overlay when open
  const sidebarCollapsed = isMobile ? !mobileMenuOpen : false;
  const mainContentMargin = isMobile ? 'ml-0' : 'ml-80';

  return (
    <div className="min-h-screen bg-white">
      {/* Sidebar */}
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
        onMobileClose={closeMobileMenu}
      />

      {/* Main Content */}
      <div className={`transition-all duration-300 ${isMobile ? 'ml-0' : mainContentMargin}`}>
        {/* Mobile Menu Button */}
        {isMobile && (
          <div className="bg-white border-b border-gray-200 px-4 py-3 shadow-sm">
            <button
              onClick={openMobileMenu}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        )}

        {/* Dashboard Content */}
        <main className="min-h-screen bg-white p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {/* Welcome Section */}
            <div className="relative mb-12">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-indigo-600/10 rounded-3xl blur-xl"></div>
                      <div className="relative rounded-3xl p-8 shadow-2xl border border-blue-200/30 overflow-hidden" style={{ backgroundColor: '#001F54' }}>
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-indigo-600/5 to-purple-600/10"></div>
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.1),transparent_50%)]"></div>
                
                <div className="relative flex items-center justify-between">
                  <div>
                    <h1 className="text-4xl font-bold bg-gradient-to-r from-white via-blue-100 to-indigo-100 bg-clip-text text-transparent drop-shadow-sm">
                      Hi, {user?.nickname || user?.name || user?.full_name || 'User'}
                    </h1>
                    <p className="text-blue-100 mt-3 text-lg font-medium">
                      Here's your dashboard overview for today
                    </p>
                    <div className="flex items-center mt-4 space-x-6">
                      <div className="flex items-center text-sm text-blue-200">
                        <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse shadow-sm"></div>
                        System Status: All Good
                      </div>
                      <div className="text-sm text-blue-200">
                        Last updated: {new Date().toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                          <div className="hidden lg:flex flex-col items-end">
                            <div className="relative inline-block">
                              <div className="w-10 h-10 bg-gradient-to-br from-white/10 to-blue-100/20 rounded-xl flex items-center justify-center shadow-lg ring-1 ring-white/20 backdrop-blur-sm">
                                <Bell className="w-5 h-5 text-white drop-shadow-lg" />
                              </div>
                              {/* Notification Count Badge */}
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center shadow-lg ring-1 ring-white">
                                <span className="text-white text-[10px] font-bold leading-none">
                                  {loading ? '...' : (notifications?.length || 0)}
                                </span>
                              </div>
                            </div>
                            {/* Date Information */}
                            <div className="mt-3 text-right w-full">
                              <p className="text-white/90 font-semibold text-sm text-right">Today</p>
                              <p className="text-white/70 text-xs text-right">
                                {new Date().toLocaleDateString('en-US', { 
                                  weekday: 'long', 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                              </p>
                            </div>
                          </div>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {/* Users Card - Shows Total Users or Technical Department Users based on role */}
              <div className="group relative">
                <div className="absolute inset-0 rounded-xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300" style={{ backgroundColor: '#001F54' }}></div>
                <div className="relative bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] overflow-hidden">
                  {/* Background decoration */}
                  <div className="absolute top-0 right-0 w-16 h-16 rounded-full -translate-y-8 translate-x-8 opacity-10" style={{ backgroundColor: '#001F54' }}></div>
                  <div className="absolute bottom-0 left-0 w-12 h-12 rounded-full translate-y-6 -translate-x-6 opacity-5" style={{ backgroundColor: '#001F54' }}></div>
                  
                  <div className="relative">
                    {/* Icon and Value */}
                    <div className="flex items-center justify-between mb-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ring-2 ring-offset-2 ring-offset-white transition-transform duration-300 group-hover:scale-110"
                        style={{ 
                          backgroundColor: '#001F54',
                          '--tw-ring-color': '#001F5430'
                        } as React.CSSProperties}
                      >
                        <Users className="w-6 h-6 text-white drop-shadow-sm" />
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold" style={{ color: '#001F54' }}>
                          {loading ? '...' : 
                            user?.roles?.some(role => role.name === 'Technical HOD') 
                              ? (technicalDeptStats?.total_users?.toLocaleString() || '0')
                              : (userStats?.total_users?.toLocaleString() || '0')
                          }
                        </p>
                        <div className="w-8 h-0.5 rounded-full ml-auto mt-1" style={{ backgroundColor: '#001F54' }}></div>
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-sm font-semibold text-gray-800">
                      {user?.roles?.some(role => role.name === 'Technical HOD') ? 'Technical Department' : 'Total Users'}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {loading ? 'Loading...' : 
                        user?.roles?.some(role => role.name === 'Technical HOD') 
                          ? `${technicalDeptStats?.roles_included?.length || 0} roles included`
                          : `${userStats?.verified_users || 0} verified, ${userStats?.unverified_users || 0} unverified`
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Library Books Card - Navy Blue Theme */}
              <div className="group relative">
                <div className="absolute inset-0 rounded-xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300" style={{ backgroundColor: '#001F54' }}></div>
                <div className="relative bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] overflow-hidden">
                  {/* Background decoration */}
                  <div className="absolute top-0 right-0 w-16 h-16 rounded-full -translate-y-8 translate-x-8 opacity-10" style={{ backgroundColor: '#001F54' }}></div>
                  <div className="absolute bottom-0 left-0 w-12 h-12 rounded-full translate-y-6 -translate-x-6 opacity-5" style={{ backgroundColor: '#001F54' }}></div>
                  
                  <div className="relative">
                    {/* Icon and Value */}
                    <div className="flex items-center justify-between mb-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ring-2 ring-offset-2 ring-offset-white transition-transform duration-300 group-hover:scale-110"
                        style={{ 
                          backgroundColor: '#001F54',
                          '--tw-ring-color': '#001F5430'
                        } as React.CSSProperties}
                      >
                        <BookOpen className="w-6 h-6 text-white drop-shadow-sm" />
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold" style={{ color: '#001F54' }}>
                          {loading ? '...' : libraryStats?.total_books?.toLocaleString() || '0'}
                        </p>
                        <div className="w-8 h-0.5 rounded-full ml-auto mt-1" style={{ backgroundColor: '#001F54' }}></div>
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-sm font-semibold text-gray-800">Library Books</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {loading ? 'Loading...' : `${libraryStats?.active_borrows || 0} active borrows`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Company Vehicles Card - Navy Blue Theme */}
              <div className="group relative">
                <div className="absolute inset-0 rounded-xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300" style={{ backgroundColor: '#001F54' }}></div>
                <div className="relative bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] overflow-hidden">
                  {/* Background decoration */}
                  <div className="absolute top-0 right-0 w-16 h-16 rounded-full -translate-y-8 translate-x-8 opacity-10" style={{ backgroundColor: '#001F54' }}></div>
                  <div className="absolute bottom-0 left-0 w-12 h-12 rounded-full translate-y-6 -translate-x-6 opacity-5" style={{ backgroundColor: '#001F54' }}></div>
                  
                  <div className="relative">
                    {/* Icon and Value */}
                    <div className="flex items-center justify-between mb-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ring-2 ring-offset-2 ring-offset-white transition-transform duration-300 group-hover:scale-110"
                        style={{ 
                          backgroundColor: '#001F54',
                          '--tw-ring-color': '#001F5430'
                        } as React.CSSProperties}
                      >
                        <Car className="w-6 h-6 text-white drop-shadow-sm" />
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold" style={{ color: '#001F54' }}>
                          {loading ? '...' : vehicleStats?.total_vehicles?.toLocaleString() || '0'}
                        </p>
                        <div className="w-8 h-0.5 rounded-full ml-auto mt-1" style={{ backgroundColor: '#001F54' }}></div>
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-sm font-semibold text-gray-800">Company Vehicles</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {loading ? 'Loading...' : `${vehicleStats?.available_vehicles || 0} available`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Pending Approvals Card - Navy Blue Theme */}
              <div className="group relative">
                <div className="absolute inset-0 rounded-xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300" style={{ backgroundColor: '#001F54' }}></div>
                <div className="relative bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] overflow-hidden">
                  {/* Background decoration */}
                  <div className="absolute top-0 right-0 w-16 h-16 rounded-full -translate-y-8 translate-x-8 opacity-10" style={{ backgroundColor: '#001F54' }}></div>
                  <div className="absolute bottom-0 left-0 w-12 h-12 rounded-full translate-y-6 -translate-x-6 opacity-5" style={{ backgroundColor: '#001F54' }}></div>
                  
                  <div className="relative">
                    {/* Icon and Value */}
                    <div className="flex items-center justify-between mb-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ring-2 ring-offset-2 ring-offset-white transition-transform duration-300 group-hover:scale-110"
                        style={{ 
                          backgroundColor: '#001F54',
                          '--tw-ring-color': '#001F5430'
                        } as React.CSSProperties}
                      >
                        <CheckCircle className="w-6 h-6 text-white drop-shadow-sm" />
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold" style={{ color: '#001F54' }}>
                          {loading ? '...' : approvalsStats?.total_pending?.toLocaleString() || '0'}
                        </p>
                        <div className="w-8 h-0.5 rounded-full ml-auto mt-1" style={{ backgroundColor: '#001F54' }}></div>
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-sm font-semibold text-gray-800">Pending Approvals</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {loading ? 'Loading...' : `${approvalsStats?.vehicle_requests_pending || 0} vehicles, ${approvalsStats?.sales_jobs_accounts_pending || 0} jobs`}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 gap-8 mb-12">
              {/* Recent Activity - Full width */}
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-r from-slate-600/10 to-blue-600/10 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/30 overflow-hidden">
                  <RecentActivity />
                </div>
              </div>
            </div>

            {/* System Metrics Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Performance Card */}
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border border-white/30 hover:shadow-3xl transition-all duration-300">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900">Performance</h3>
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <BarChart3 className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">CPU Usage</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              (systemMetrics?.cpu_usage_percent || 0) < 50 
                                ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
                                : (systemMetrics?.cpu_usage_percent || 0) < 80 
                                ? 'bg-gradient-to-r from-yellow-500 to-orange-500' 
                                : 'bg-gradient-to-r from-red-500 to-pink-500'
                            }`}
                            style={{ width: `${systemMetrics?.cpu_usage_percent || 0}%` }}
                          ></div>
                        </div>
                        <span className={`font-bold ${
                          (systemMetrics?.cpu_usage_percent || 0) < 50 
                            ? 'text-green-600' 
                            : (systemMetrics?.cpu_usage_percent || 0) < 80 
                            ? 'text-yellow-600' 
                            : 'text-red-600'
                        }`}>
                          {loading ? '...' : `${Math.round(systemMetrics?.cpu_usage_percent || 0)}%`}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Memory</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              (systemMetrics?.memory_usage_percent || 0) < 50 
                                ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
                                : (systemMetrics?.memory_usage_percent || 0) < 80 
                                ? 'bg-gradient-to-r from-yellow-500 to-orange-500' 
                                : 'bg-gradient-to-r from-red-500 to-pink-500'
                            }`}
                            style={{ width: `${systemMetrics?.memory_usage_percent || 0}%` }}
                          ></div>
                        </div>
                        <span className={`font-bold ${
                          (systemMetrics?.memory_usage_percent || 0) < 50 
                            ? 'text-green-600' 
                            : (systemMetrics?.memory_usage_percent || 0) < 80 
                            ? 'text-yellow-600' 
                            : 'text-red-600'
                        }`}>
                          {loading ? '...' : `${Math.round(systemMetrics?.memory_usage_percent || 0)}%`}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Disk Space</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              (systemMetrics?.disk_usage_percent || 0) < 50 
                                ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
                                : (systemMetrics?.disk_usage_percent || 0) < 80 
                                ? 'bg-gradient-to-r from-yellow-500 to-orange-500' 
                                : 'bg-gradient-to-r from-red-500 to-pink-500'
                            }`}
                            style={{ width: `${systemMetrics?.disk_usage_percent || 0}%` }}
                          ></div>
                        </div>
                        <span className={`font-bold ${
                          (systemMetrics?.disk_usage_percent || 0) < 50 
                            ? 'text-green-600' 
                            : (systemMetrics?.disk_usage_percent || 0) < 80 
                            ? 'text-yellow-600' 
                            : 'text-red-600'
                        }`}>
                          {loading ? '...' : `${Math.round(systemMetrics?.disk_usage_percent || 0)}%`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* System Status Card */}
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/20 to-green-600/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border border-white/30 hover:shadow-3xl transition-all duration-300">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900">System Status</h3>
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Uptime</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="font-bold text-green-600">
                          {loading ? '...' : `${((performanceData?.api_performance?.uptime || 0) / 86400 * 100).toFixed(1)}%`}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Response Time</span>
                      <span className={`font-bold ${
                        (performanceData?.api_performance?.average_response_time || 0) < 200 
                          ? 'text-green-600' 
                          : (performanceData?.api_performance?.average_response_time || 0) < 500 
                          ? 'text-yellow-600' 
                          : 'text-red-600'
                      }`}>
                        {loading ? '...' : `${Math.round(performanceData?.api_performance?.average_response_time || 120)}ms`}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Cache Hit Rate</span>
                      <span className={`font-bold ${
                        (performanceData?.summary?.cache_hit_rate || 0) > 70 
                          ? 'text-green-600' 
                          : (performanceData?.summary?.cache_hit_rate || 0) > 50 
                          ? 'text-yellow-600' 
                          : 'text-red-600'
                      }`}>
                        {loading ? '...' : `${Math.round(performanceData?.summary?.cache_hit_rate || 0)}%`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Security Card */}
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-indigo-600/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border border-white/30 hover:shadow-3xl transition-all duration-300">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900">Security</h3>
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Failed Logins</span>
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${
                          (securityStats?.failed_logins || 0) === 0 
                            ? 'bg-green-500' 
                            : (securityStats?.failed_logins || 0) < 10 
                            ? 'bg-yellow-500' 
                            : 'bg-red-500'
                        }`}></div>
                        <span className={`font-bold ${
                          (securityStats?.failed_logins || 0) === 0 
                            ? 'text-green-600' 
                            : (securityStats?.failed_logins || 0) < 10 
                            ? 'text-yellow-600' 
                            : 'text-red-600'
                        }`}>
                          {loading ? '...' : securityStats?.failed_logins || 0}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Security Score</span>
                      <div className="flex items-center space-x-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          securityStats?.security_score === 'A+' 
                            ? 'bg-green-500' 
                            : securityStats?.security_score === 'A' 
                            ? 'bg-blue-500' 
                            : 'bg-yellow-500'
                        }`}>
                          <span className="text-white text-xs font-bold">
                            {loading ? '...' : securityStats?.security_score || 'A+'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Last Scan</span>
                      <span className="font-bold text-gray-700">
                        {loading ? '...' : securityStats?.last_scan ? new Date(securityStats.last_scan).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ' ago' : '1 hour ago'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}